import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { errorMessageAdapter } from '../../../../adapters';
import { Card } from '../../../../components/atoms';
import { AuthService } from '../../../../services/AuthService';
import { useAuth } from '../../../../store/auth';
import { errorToast, successToast } from '../../../../utils/toasts';
import { useApi } from '../../../../services/hooks/apiHook';
import { AxiosInstance } from 'axios';
import { MainCustomButton } from '../../../../components/Buttons';

export type FormDataType = {
  password: string;
  password_confirmation: string;
};

const ChangePasswordCard = () => {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const api: AxiosInstance = useApi();

  const validationSchema = Yup.object().shape({
    /* eslint-disable no-useless-escape */
    password: Yup.string()
      .required(t('OnboardingCreateAccountErrorPasswordRequired') || '')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
        t('OnboardingCreateAccountErrorPasswordRule') || ''
      ),
    password_confirmation: Yup.string()
      .required(t('OnboardingCreateAccountErrorConfirmPasswordRequired') || '')
      .oneOf([Yup.ref('password')], t('OnboardingCreateAccountErrorConfirmPasswordMustMatch') || '')
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset
  } = useForm<FormDataType>({
    resolver: yupResolver(validationSchema)
  });

  const handleChange = async (data: FormDataType) => {
    try {
      await AuthService.changePassword(
        api,
        {
          ...data,
          email: user!.email,
          user_slug: user!.slug
        },
        { headers: { 'Content-Language': i18n.language.toLowerCase(), Authorization: `Bearer ${token}` } }
      );

      reset();
      successToast('Password changed');
    } catch (e) {
      errorToast(errorMessageAdapter(e));
    }
  };

  return (
    <Card.Root className="w-full">
      <Card.Header>
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold">{t('titles.ttlEditPassWord')}</h3>
          <p className="text-sm text-gray-500">{t('preferences.updatePasswordDescription')}</p>
        </div>
      </Card.Header>

      <Card.Content>
        <form className="flex flex-col gap-5" onSubmit={handleSubmit(handleChange)}>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="flex w-full flex-col gap-2">
              <label className={`${errors.password?.message ? 'text-red-500' : 'text-gray-700'} text-sm font-medium`}>
                {t('labels.lblPassword')}*
              </label>
              <input
                className="h-[44px] w-full rounded-lg border-[1px] border-gray-300 px-4 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder={t('preferences.enterNewPassword') as string}
                type="password"
                {...register('password')}
              ></input>
              {errors.password?.message && (
                <span className="text-xs text-red-500">{errors.password.message}</span>
              )}
            </div>

            <div className="flex w-full flex-col gap-2">
              <label className={`${errors.password_confirmation?.message ? 'text-red-500' : 'text-gray-700'} text-sm font-medium`}>
                {t('labels.lblRepeatPassWord')}*
              </label>
              <input
                className="h-[44px] w-full rounded-lg border-[1px] border-gray-300 px-4 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder={t('preferences.confirmNewPassword') as string}
                type="password"
                {...register('password_confirmation')}
              ></input>
              {errors.password_confirmation?.message && (
                <span className="text-xs text-red-500">{errors.password_confirmation.message}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-lg bg-blue-50 p-3">
            <svg className="h-5 w-5 flex-shrink-0 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="text-xs text-gray-600">
              {t('preferences.passwordRequirements')}
            </p>
          </div>

          <div className="flex justify-end">
            <MainCustomButton text={t('buttons.btnResetPassWord')} />
          </div>
        </form>
      </Card.Content>
    </Card.Root>
  );
};

export { ChangePasswordCard };
